<template>
    <div class="quick-search__wrap">
        <div class="quick-search__input-wrap">
            <input ref="searchInput" v-model="searchTerm" type="text" :placeholder="translations?.TypeToSearch" class="secondary-nav__search-input quick-search__input" tabindex="8" @keyup="queryTheTerm" @keypress="searchProducts" />
            <button ref="searchButton" class="icon-search secondary-nav__search-button quick-search__button" @click="queryTheTerm" aria-label="Search Icon" title="Search"></button>
        </div>
        <div class="quick-search__search-panel" :style="`top: ${headerHeight}`" ref="searchPanel" v-if="searchTerm" v-click-outside="onClickOutside">
            <div class="container">
                <div class="row py-5">
                    <div class="col-md-6 mb-4 mb-md-0">
                        <div class="h5">{{ translations?.Pages }}</div>
                        <div v-if="pages.length">
                            <div v-for="page in pages">
                                <!-- Card for each page result -->
                                <PagesSearchCard :url="page.url" :contentType="page.contentType" :kicker="page.contentTypeDisplay" :title="page.highlightResults.title.value"> </PagesSearchCard>
                            </div>
                            <a :href="`/search?FilterCriteria.Query=${searchTerm}&FilterCriteria.Tab=pages`" class="quick-search__view-all">{{ translations?.ViewAllMatchingPages }} <i class="icon-arrow-right-hover"></i></a>
                        </div>
                        <div v-else>
                            <div>{{ translations?.NoPagesFound }}</div>
                        </div>
                    </div>
                    <div class="col-md-6 quick-search__panel-border">
                        <div class="h5 quick-search__products-heading">{{ translations?.Products }}</div>
                        <div v-if="products.length">
                            <div v-for="product in products">
                                <!-- Card for each product result -->
                                <ProductsSearchCard
                                    :url="product.url"
                                    :name="product.displayName"
                                    :translations="translations"
                                    :primaryImageUrl="product.primaryImageUrl"
                                    :stockNumber="product.highlightResults?.stockNumber?.value"
                                    :displayName="product.highlightResults?.displayName?.value"
                                    :additionalDisplayName="product.highlightResults?.additionalDisplayName?.value"
                                >
                                </ProductsSearchCard>
                            </div>
                            <a :href="`/search?FilterCriteria.Query=${searchTerm}&FilterCriteria.Tab=products`" class="quick-search__view-all">{{ translations?.ViewAllMatchingProducts }} <i class="icon-arrow-right-hover"></i></a>
                        </div>
                        <div v-else>
                            <div>{{ translations?.NoProductsFound }}</div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import { GetSearchData } from 'Api/apis';
import { throttle } from 'throttle-debounce';
import { IQuickSearchPages, IQuickSearchProducts, IQuickSearchTranslations } from 'Vue/interfaces/Interfaces';
import PagesSearchCard from 'Vue/components/searchCardTypes/PagesSearchCard.vue';
import ProductsSearchCard from 'Vue/components/searchCardTypes/ProductsSearchCard.vue';
import vClickOutside from 'click-outside-vue3';

export default {
    name: 'Search',
    components: {
        ProductsSearchCard,
        PagesSearchCard,
    },
    data: () => {
        return {
            searchTerm: '',
            pages: [] as IQuickSearchPages[],
            products: [] as IQuickSearchProducts[],
            headerHeight: 0,
            translations: {} as IQuickSearchTranslations,
        };
    },
    directives: {
        /**
         * Click Outside is a vue 3 directive that will perform a function
         * when something outside of the element with the vue directive is clicked.
         * https://www.npmjs.com/package/click-outside-vue3
         */
        clickOutside: vClickOutside.directive,
    },
    methods: {
        /**
         * When a query is run, hit the endpoint and set the pages and products data used in our components
         */
        async queryTheTerm() {
            let searchData = await GetSearchData(this.searchTerm);

            this.pages = searchData.pages?.length ? searchData.pages : [];
            this.products = searchData.products?.length ? searchData.products : [];
        },
        /**
         * Search for products when user hits ENTER
         * @param e Key Event
         */
        searchProducts(e) {
            if (e.key == 'Enter' && this.searchTerm) {
                window.location.href = `/search?FilterCriteria.Query=${this.searchTerm}&FilterCriteria.Tab=products`;
            }
        },
        /**
         * When clicked outside of search dropdown clear our our search term (clearing out searchterm also closes search dropdown)
         */
        onClickOutside() {
            this.searchTerm = '';
        },
        getHeaderHeight() {
            let header: HTMLElement = document.querySelector('.site-header');
            return header.offsetHeight;
        },
        /**
         * Our header height changes at different breakpoints and dropdown positioning needs to be re-calculated
         */
        checkWidth() {
            if (window.outerWidth >= 768) {
                this.headerHeight = `${this.getHeaderHeight()}px`;
            } else if (window.outerWidth < 768) {
                this.headerHeight = `auto`;
            }
        },
        /**
         * Our Sitefinity Translations for the search app are located in SitefinityWebApp/ResourcePackages/BaldwinHardware/MVC/Views/Navigation/NavigationView.SecondaryNav.cshtml
         * We are pulling them into our app here for use
         */
        getTranslations() {
            let quickSearchDataContainer = document.getElementById('quickSearchTranslations');
            return JSON.parse(quickSearchDataContainer.getAttribute('data-translations'));
        },
    },
    created() {
        this.translations = this.getTranslations();
        this.checkWidth();
        window.addEventListener(
            'resize',
            throttle(100, false, () => {
                this.checkWidth();
            })
        );
    },
};
</script>
